import React from 'react';
import { Offcanvas, OffcanvasHeader, OffcanvasBody, Form, FormGroup, Label, Input, Button, FormFeedback } from 'reactstrap';
import AsyncSelect from 'react-select/async';
import { useCreateTask } from '../hooks/useCreateTask';
import { TASK_TYPE, TASK_TYPE_OPTIONS, TASK_PRIORITY_OPTIONS, CONTACT_REASON_OPTIONS, CONTACT_OUTCOME_LABELS, CONTACT_REASON_LABELS, TASK_STATUS_LABELS } from '../../../../constants/tasks';

const STATUS_COLOR = {
    new: '#3b82f6', scheduled: '#64748b', doing: '#7c3aed',
    waiting: '#7c3aed', overdue: '#ef4444', done: '#22c55e', archived: '#94a3b8',
};

const fmtDate = (ts) => {
    if (!ts) return '';
    try {
        const d = ts.toDate ? ts.toDate() : new Date(ts);
        return new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' }).format(d);
    } catch { return ''; }
};

// Custom components to avoid default broken SVG icons/indicators in React-Select
const CustomDropdownIndicator = (props) => {
    return (
        <div {...props.innerProps} style={{ padding: '0 8px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
            <i className="mdi mdi-chevron-down" style={{ color: '#482712', fontSize: '18px' }}></i>
        </div>
    );
};

const CustomClearIndicator = (props) => {
    return (
        <div {...props.innerProps} style={{ padding: '0 8px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
            <i className="mdi mdi-close" style={{ color: '#784b22', fontSize: '16px' }}></i>
        </div>
    );
};

const CustomLoadingIndicator = (props) => {
    return (
        <div {...props.innerProps} style={{ padding: '0 8px', display: 'flex', alignItems: 'center' }}>
            <i className="mdi mdi-loading mdi-spin" style={{ color: '#e2661c', fontSize: '16px' }}></i>
        </div>
    );
};

const CreateTaskSidebar = ({ isOpen, toggle, onTaskCreated }) => {

    const { formik, members, employees, loadingResources, memberHistory, handleMemberChange } = useCreateTask(onTaskCreated, toggle);

    // Mapeamento e Debounce Helper
    const normalizeStr = (s) =>
        (s || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

    const memberOptions = members.map(m => ({
        value: m.id,
        label: m.fullName || m.name || "",
        searchName: m.searchName || normalizeStr(m.fullName || m.name),
    }));
    const employeeOptions = employees.map(e => ({
        value: e.id,
        label: e.fullName || e.name || "",
        searchName: e.searchName || normalizeStr(e.fullName || e.name),
    }));

    const loadMemberOptions = (inputValue, callback) => {
        setTimeout(() => {
            const q = normalizeStr(inputValue);
            const filtered = memberOptions.filter(i =>
                i.searchName.includes(q) || normalizeStr(i.label).includes(q)
            );
            callback(filtered);
        }, 600);
    };

    const loadEmployeeOptions = (inputValue, callback) => {
        setTimeout(() => {
            const q = normalizeStr(inputValue);
            const filtered = employeeOptions.filter(i =>
                i.searchName.includes(q) || normalizeStr(i.label).includes(q)
            );
            callback(filtered);
        }, 600);
    };

    // Estilização customizada premium para o React-Select
    const customSelectStyles = {
        control: (base, state) => ({
            ...base,
            backgroundColor: '#fef5ef',
            borderColor: state.isFocused ? '#e2661c' : '#482712',
            borderWidth: '2px',
            borderRadius: '12px',
            minHeight: '46px',
            boxShadow: 'none',
            fontFamily: "'Outfit', sans-serif",
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
                borderColor: '#e2661c'
            }
        }),
        menu: (base) => ({
            ...base,
            backgroundColor: '#ffffff',
            border: '2px solid #482712',
            borderRadius: '12px',
            boxShadow: '0 8px 24px rgba(72, 39, 18, 0.15)',
            overflow: 'hidden',
            zIndex: 99999
        }),
        menuList: (base) => ({
            ...base,
            padding: 0
        }),
        option: (base, state) => ({
            ...base,
            fontFamily: "'Outfit', sans-serif",
            fontWeight: '700',
            fontSize: '13px',
            color: state.isSelected ? '#ffffff' : '#482712',
            backgroundColor: state.isSelected 
                ? '#e2661c' 
                : state.isFocused 
                    ? '#fef5ef' 
                    : 'transparent',
            cursor: 'pointer',
            padding: '10px 14px',
            transition: 'all 0.15s ease',
            '&:active': {
                backgroundColor: '#ebdcc5'
            }
        }),
        placeholder: (base) => ({
            ...base,
            color: '#784b22',
            opacity: 0.6,
            fontWeight: '600',
            fontSize: '13.5px'
        }),
        singleValue: (base) => ({
            ...base,
            color: '#482712',
            fontWeight: '600'
        }),
        multiValue: (base) => ({
            ...base,
            backgroundColor: '#ebdcc5',
            border: '1px solid rgba(72, 39, 18, 0.15)',
            borderRadius: '6px',
            padding: '1px 4px'
        }),
        multiValueLabel: (base) => ({
            ...base,
            color: '#482712',
            fontFamily: "'Outfit', sans-serif",
            fontWeight: '800',
            fontSize: '11.5px',
            textTransform: 'uppercase'
        }),
        multiValueRemove: (base) => ({
            ...base,
            color: '#784b22',
            borderRadius: '4px',
            cursor: 'pointer',
            transition: 'all 0.15s ease',
            '&:hover': {
                backgroundColor: '#482712',
                color: '#ffffff'
            }
        }),
        dropdownIndicator: (base) => ({
            ...base,
            color: '#482712',
            '&:hover': {
                color: '#e2661c'
            }
        }),
        clearIndicator: (base) => ({
            ...base,
            color: '#784b22',
            '&:hover': {
                color: '#ef4444'
            }
        }),
        indicatorSeparator: () => ({
            display: 'none'
        })
    };

    return (
        <Offcanvas isOpen={isOpen} toggle={toggle} direction="end" style={{ width: "450px" }}>
            <OffcanvasHeader toggle={toggle} className="border-bottom pb-3 bg-light-subtle">
                <span className="fw-bold text-dark font-size-16">Criar Nova Tarefa</span>
            </OffcanvasHeader>
            <OffcanvasBody className="p-0 bg-white d-flex flex-column h-100" style={{ overflow: "hidden" }}>
                <Form 
                    className="flex-grow-1 p-4 custom-scrollbar" 
                    onSubmit={formik.handleSubmit}
                    style={{ overflowY: "auto", maxHeight: "calc(100vh - 180px)" }}
                >
                    <FormGroup className="mb-4">
                        <Label className="fw-semibold text-muted font-size-13">Título da Tarefa <span className="text-danger">*</span></Label>
                        <Input
                            type="text"
                            name="title"
                            placeholder="Ex: Ligar para novo lead"
                            className="shadow-sm border-light"
                            value={formik.values.title}
                            onChange={formik.handleChange}
                            invalid={formik.touched.title && !!formik.errors.title}
                        />
                        <FormFeedback>{formik.errors.title}</FormFeedback>
                    </FormGroup>

                    <FormGroup className="mb-4">
                        <Label className="fw-semibold text-muted font-size-13">Descrição</Label>
                        <Input
                            type="textarea"
                            name="description"
                            rows="4"
                            placeholder={
                                formik.values.type === 'contact' && formik.values.contactReason === 'trial_followup'
                                    ? 'Ex: "Entrou em contato perguntando sobre a turma. Enviou tabela de horários e valores. Aguardando resposta." — ou — "Agendou aula experimental para [data]. Não compareceu."'
                                    : 'Detalhes da pendência...'
                            }
                            className="shadow-sm border-light"
                            value={formik.values.description}
                            onChange={formik.handleChange}
                        />
                        {formik.values.type === 'contact' && formik.values.contactReason === 'trial_followup' && (
                            <small className="text-muted d-block mt-1" style={{ fontSize: '11px', lineHeight: '1.4' }}>
                                <i className="mdi mdi-information-outline me-1" style={{ color: '#e2661c' }}></i>
                                A IA usa a descrição para escolher a mensagem certa. Indique se o lead <strong>recebeu informações e aguarda resposta</strong> ou se <strong>agendou e faltou</strong>.
                            </small>
                        )}
                    </FormGroup>

                    <div className="row">
                        <div className="col-12 col-md-6">
                            <FormGroup className="mb-4">
                                <Label className="fw-semibold text-muted font-size-13">Data <span className="text-danger">*</span></Label>
                                <Input
                                    type="date"
                                    name="scheduledDate"
                                    className="shadow-sm border-light"
                                    value={formik.values.scheduledDate}
                                    onChange={formik.handleChange}
                                    invalid={formik.touched.scheduledDate && !!formik.errors.scheduledDate}
                                />
                                <FormFeedback>{formik.errors.scheduledDate}</FormFeedback>
                            </FormGroup>
                        </div>
                        <div className="col-12 col-md-6">
                            <FormGroup className="mb-4">
                                <Label className="fw-semibold text-muted font-size-13">Hora</Label>
                                <Input
                                    type="time"
                                    name="scheduledTime"
                                    className="shadow-sm border-light"
                                    value={formik.values.scheduledTime}
                                    onChange={formik.handleChange}
                                />
                            </FormGroup>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-12 col-md-6">
                            <FormGroup className="mb-4">
                                <Label className="fw-semibold text-muted font-size-13">Tipo <span className="text-danger">*</span></Label>
                                <Input
                                    type="select"
                                    name="type"
                                    className="shadow-sm border-light"
                                    value={formik.values.type}
                                    onChange={formik.handleChange}
                                    invalid={formik.touched.type && !!formik.errors.type}
                                >
                                    {TASK_TYPE_OPTIONS.map((opt) => (
                                        <option key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </option>
                                    ))}
                                </Input>
                                <FormFeedback>{formik.errors.type}</FormFeedback>
                            </FormGroup>
                        </div>
                        <div className="col-12 col-md-6">
                            <FormGroup className="mb-4">
                                <Label className="fw-semibold text-muted font-size-13">Prioridade <span className="text-danger">*</span></Label>
                                <Input
                                    type="select"
                                    name="priority"
                                    className="shadow-sm border-light"
                                    value={formik.values.priority}
                                    onChange={formik.handleChange}
                                    invalid={formik.touched.priority && !!formik.errors.priority}
                                >
                                    {TASK_PRIORITY_OPTIONS.map((opt) => (
                                        <option key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </option>
                                    ))}
                                </Input>
                                <FormFeedback>{formik.errors.priority}</FormFeedback>
                            </FormGroup>
                        </div>
                    </div>

                    {/* Motivo do Contato (visível apenas para tarefas do tipo Contato) */}
                    {formik.values.type === TASK_TYPE.CONTACT && (
                        <FormGroup className="mb-4">
                            <Label className="fw-semibold text-muted font-size-13">Motivo do Contato</Label>
                            <Input
                                type="select"
                                name="contactReason"
                                className="shadow-sm border-light"
                                value={formik.values.contactReason}
                                onChange={formik.handleChange}
                            >
                                <option value="">Selecione o motivo (opcional)</option>
                                {CONTACT_REASON_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </option>
                                ))}
                            </Input>
                        </FormGroup>
                    )}

                    <FormGroup className="mb-4">
                        <Label className="fw-semibold text-muted font-size-13 d-flex align-items-center">
                            Cliente Relacionado
                            {loadingResources && <i className="mdi mdi-loading mdi-spin ms-2 text-primary" style={{ fontSize: '14px' }}></i>}
                        </Label>
                        <div className="neo-select-wrapper">
                            <AsyncSelect
                                cacheOptions
                                defaultOptions={memberOptions}
                                loadOptions={loadMemberOptions}
                                placeholder="Digite o nome..."
                                noOptionsMessage={() => "Nenhum cliente encontrado"}
                                loadingMessage={() => "Carregando..."}
                                isClearable
                                classNamePrefix="select"
                                value={memberOptions.find(o => o.value === formik.values.selectedMember) || null}
                                onChange={handleMemberChange}
                                isDisabled={loadingResources}
                                styles={customSelectStyles}
                                components={{
                                    DropdownIndicator: CustomDropdownIndicator,
                                    ClearIndicator: CustomClearIndicator,
                                    IndicatorSeparator: null,
                                    LoadingIndicator: CustomLoadingIndicator
                                }}
                            />
                        </div>
                    </FormGroup>

                    {/* Painel de histórico do membro */}
                    {memberHistory.loading && (
                        <div className="d-flex align-items-center gap-2 mb-3 text-muted font-size-13">
                            <i className="mdi mdi-loading mdi-spin" style={{ color: '#e2661c' }}></i>
                            Buscando interações anteriores...
                        </div>
                    )}

                    {!memberHistory.loading && memberHistory.active.length > 0 && (
                        <div className="mb-4 p-3 rounded-3" style={{ background: '#fff3cd', border: '1.5px solid #f0c14b' }}>
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <i className="mdi mdi-alert" style={{ color: '#b45309', fontSize: '16px' }}></i>
                                <span className="fw-bold font-size-13" style={{ color: '#92400e' }}>
                                    Este aluno já tem {memberHistory.active.length === 1 ? 'um card ativo' : `${memberHistory.active.length} cards ativos`} no board!
                                </span>
                            </div>
                            {memberHistory.active.map(t => (
                                <div key={t.id} className="d-flex align-items-center gap-2 mt-1 font-size-12" style={{ color: '#78350f' }}>
                                    <span
                                        className="rounded-1 px-2 py-0 fw-bold text-white font-size-11"
                                        style={{ background: STATUS_COLOR[t.status] || '#64748b', whiteSpace: 'nowrap' }}
                                    >
                                        {TASK_STATUS_LABELS[t.status] || t.status}
                                    </span>
                                    <span className="text-truncate">{t.title}</span>
                                    <span className="ms-auto text-muted" style={{ whiteSpace: 'nowrap' }}>{fmtDate(t.scheduledDate)}</span>
                                </div>
                            ))}
                        </div>
                    )}

                    {!memberHistory.loading && memberHistory.past.length > 0 && (
                        <div className="mb-4">
                            <p className="fw-semibold font-size-12 text-muted mb-2 text-uppercase" style={{ letterSpacing: '0.05em' }}>
                                Interações anteriores
                            </p>
                            <div className="d-flex flex-column gap-2">
                                {memberHistory.past.map(t => (
                                    <div
                                        key={t.id}
                                        className="p-2 rounded-2"
                                        style={{ background: '#f8f9fa', border: '1px solid #e9ecef' }}
                                    >
                                        <div className="d-flex align-items-center justify-content-between mb-1">
                                            <span className="fw-semibold font-size-12 text-truncate" style={{ color: '#482712', maxWidth: '65%' }}>
                                                {t.title}
                                            </span>
                                            <span className="font-size-11 text-muted">{fmtDate(t.createdAt)}</span>
                                        </div>
                                        <div className="d-flex align-items-center gap-2 flex-wrap">
                                            {t.contactReason && (
                                                <span className="font-size-11 text-muted">
                                                    {CONTACT_REASON_LABELS[t.contactReason] || t.contactReason}
                                                </span>
                                            )}
                                            {t.contactOutcome && (
                                                <>
                                                    {t.contactReason && <span className="text-muted">·</span>}
                                                    <span className="font-size-11 fw-semibold" style={{ color: '#7c3aed' }}>
                                                        {CONTACT_OUTCOME_LABELS[t.contactOutcome] || t.contactOutcome}
                                                    </span>
                                                </>
                                            )}
                                        </div>
                                        {t.outcomeNotes && (
                                            <p className="mb-0 mt-1 font-size-11 text-muted text-truncate" title={t.outcomeNotes}>
                                                {t.outcomeNotes}
                                            </p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <FormGroup className="mb-4">
                        <Label className="fw-semibold text-muted font-size-13 d-flex align-items-center">
                            Colaborador Relacionado
                            {loadingResources && <i className="mdi mdi-loading mdi-spin ms-2 text-primary" style={{ fontSize: '14px' }}></i>}
                        </Label>
                        <div className="neo-select-wrapper">
                            <AsyncSelect
                                isMulti
                                cacheOptions
                                defaultOptions={employeeOptions}
                                loadOptions={loadEmployeeOptions}
                                placeholder="Selecione um ou mais..."
                                noOptionsMessage={() => "Nenhum colaborador encontrado"}
                                loadingMessage={() => "Carregando..."}
                                isClearable
                                classNamePrefix="select"
                                value={employeeOptions.filter(o => (formik.values.selectedEmployees || []).includes(o.value))}
                                onChange={options => formik.setFieldValue('selectedEmployees', options ? options.map(o => o.value) : [])}
                                isDisabled={loadingResources}
                                styles={customSelectStyles}
                                components={{
                                    DropdownIndicator: CustomDropdownIndicator,
                                    ClearIndicator: CustomClearIndicator,
                                    IndicatorSeparator: null,
                                    LoadingIndicator: CustomLoadingIndicator
                                }}
                            />
                        </div>
                    </FormGroup>
                </Form>

                <div className="mt-auto border-top p-4 bg-white" style={{ zIndex: 5 }}>
                    <Button
                        color="primary"
                        className="w-100 py-3 fw-bold shadow rounded-3 font-size-15 transition-all hover-scale"
                        onClick={formik.submitForm}
                        disabled={formik.isSubmitting}
                    >
                        {formik.isSubmitting ? (
                            <><i className="mdi mdi-loading mdi-spin me-2" style={{ fontSize: '14px' }}></i> Salvando...</>
                        ) : (
                            <>Salvar</>
                        )}
                    </Button>
                    <Button color="light" onClick={toggle} className="w-100 mt-2 py-2 fw-semibold rounded-3 font-size-14 text-muted" disabled={formik.isSubmitting}>
                        Cancelar
                    </Button>
                </div>
            </OffcanvasBody>
        </Offcanvas>
    );
};

export default CreateTaskSidebar;
